import React, { useEffect, useState, createElement } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, EyeIcon, Loader2Icon, CopyIcon, CheckIcon, ExternalLinkIcon, HistoryIcon, CodeIcon } from 'lucide-react';
import { CodeEditor } from '../components/CodeEditor';
import { PreviewModal } from '../components/PreviewModal';
import { FormPreview } from '../components/FormPreview';
import { FormBuilder } from '../components/builder/FormBuilder';
import { GptCodeAssistant } from '../components/GptCodeAssistant';
import { VersionHistoryPanel } from '../components/VersionHistoryPanel';
import { ViewCodeModal } from '../components/ViewCodeModal';
import { auditService } from '../services/auditService';
import { FormBlock, FormTheme, FormBuilderSchema, DEFAULT_FORM_THEME, LANDING_PAGE_PALETTE } from '../components/builder/types';
import { landingPagesApi, formVersionsApi, FormVersionRecord } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
import { ConfirmModal } from '../components/ConfirmModal';
function toSlug(str: string): string {
  return str.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
}
export function LandingPageEditor() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion,
    currentUser
  } = useWorkspace();
  const isEditing = !!id && id !== 'new';
  // FIX: Team is always the workspace name, Region is the sub-region
  const team = currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const region = currentSubRegion || '';
  const [showPreview, setShowPreview] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showBuilderConfirm, setShowBuilderConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editorMode, setEditorMode] = useState<'code' | 'builder'>('code');
  const [builderBlocks, setBuilderBlocks] = useState<FormBlock[]>([]);
  const [builderTheme, setBuilderTheme] = useState<FormTheme>(DEFAULT_FORM_THEME);
  const [pageName, setPageName] = useState('');
  const [urlSlug, setUrlSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [status, setStatus] = useState('active');
  const [pageData, setPageData] = useState({
    html: '',
    css: '',
    js: ''
  });
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [activeTab, setActiveTab] = useState<'html' | 'css' | 'js'>('html');
  // Auto-generate slug from name
  useEffect(() => {
    if (!slugManuallyEdited && !isEditing) {
      setUrlSlug(toSlug(pageName));
    }
  }, [pageName, slugManuallyEdited, isEditing]);
  // Load existing page when editing
  useEffect(() => {
    if (isEditing && id) {
      setIsLoading(true);
      landingPagesApi.get(id).then(record => {
        if (record) {
          setPageName(record.Page_Name || '');
          setUrlSlug(record.Url_Slug || '');
          setSlugManuallyEdited(true);
          setStatus(record.Status || 'active');
          setPageData({
            html: record.HTML || '',
            css: record.CSS || '',
            js: record.JavaScript1 || ''
          });
          // Restore builder state if available
          if (record.Builder_Mode === 'builder' && record.Builder_Schema) {
            try {
              const schema: FormBuilderSchema = JSON.parse(record.Builder_Schema);
              setBuilderBlocks(schema.blocks || []);
              setBuilderTheme({
                ...DEFAULT_FORM_THEME,
                ...(schema.theme || {})
              });
              setEditorMode('builder');
            } catch {
              /* fallback to code mode */
            }
          } else if (record.Builder_Schema) {
            try {
              const schema: FormBuilderSchema = JSON.parse(record.Builder_Schema);
              setBuilderBlocks(schema.blocks || []);
              setBuilderTheme({
                ...DEFAULT_FORM_THEME,
                ...(schema.theme || {})
              });
            } catch {
              /* ignore */
            }
          }
        } else {
          toast.error('Landing page not found');
          navigate('/landing-pages');
        }
      }).catch(err => {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        toast.error('Failed to load landing page', {
          description: msg
        });
      }).finally(() => setIsLoading(false));
    }
  }, [id, isEditing, navigate]);
  const handleSave = async () => {
    if (!pageName.trim()) {
      toast.error('Page Name is required');
      return;
    }
    if (!urlSlug.trim()) {
      toast.error('URL Slug is required');
      return;
    }
    setIsSaving(true);
    const payload: Record<string, any> = {
      Page_Name: pageName,
      Url_Slug: urlSlug,
      Status: status,
      HTML: pageData.html,
      CSS: pageData.css,
      JavaScript1: pageData.js,
      Team: team,
      Region: region,
      Builder_Mode: editorMode
    };
    // Save builder schema
    if (builderBlocks.length > 0) {
      payload.Builder_Schema = JSON.stringify({
        blocks: builderBlocks,
        theme: builderTheme
      });
    } else if (editorMode === 'builder') {
      payload.Builder_Schema = JSON.stringify({
        blocks: [],
        theme: builderTheme
      });
    }
    try {
      let savedPageId = id || '';
      if (isEditing && id) {
        await landingPagesApi.update(id, payload);
        savedPageId = id;
        toast.success('Landing page updated', {
          description: `"${pageName}" has been updated.`
        });
        (async () => {
          try {
            const versions = await formVersionsApi.listByFormId(id);
            // Deactivate all existing versions
            for (const v of versions) {
              if (v.Is_Active === true) {
                try {
                  await formVersionsApi.update(v.ID, {
                    Is_Active: false
                  });
                } catch (e) {
                  console.warn('Failed to deactivate version:', e);
                }
              }
            }
            const nextVersion = versions.length > 0 ? Math.max(...versions.map(v => v.Version_Number || 0)) + 1 : 1;
            await formVersionsApi.create({
              Form_ID: id,
              Form_Name: pageName,
              Version_Number: nextVersion,
              HTML: pageData.html,
              CSS: pageData.css,
              JavaScript1: pageData.js,
              Builder_Schema: payload.Builder_Schema || '',
              Created_By_Name: currentUser.User_Name,
              Created_By_Email: currentUser.Email,
              Change_Summary: `Updated landing page — Status: ${status}`,
              Team: team,
              Region: region,
              Is_Active: true
            });
          } catch (err) {
            console.error('Failed to create form version:', err);
          }
          auditService.log({
            actionType: 'updated',
            entityType: 'landing_page',
            entityId: id,
            entityName: pageName,
            userName: currentUser.User_Name,
            userEmail: currentUser.Email,
            userRole: currentUser.Role,
            team: currentWorkspace.name,
            region: currentSubRegion || ''
          });
        })();
      } else {
        const result = await landingPagesApi.create(payload);
        if (result?.data?.ID) {
          savedPageId = String(result.data.ID);
        }
        toast.success('Landing page created', {
          description: `"${pageName}" has been saved.`
        });
        (async () => {
          try {
            await formVersionsApi.create({
              Form_ID: savedPageId,
              Form_Name: pageName,
              Version_Number: 1,
              HTML: pageData.html,
              CSS: pageData.css,
              JavaScript1: pageData.js,
              Builder_Schema: payload.Builder_Schema || '',
              Created_By_Name: currentUser.User_Name,
              Created_By_Email: currentUser.Email,
              Change_Summary: `Initial version — Status: ${status}`,
              Team: team,
              Region: region,
              Is_Active: true
            });
          } catch (err) {
            console.error('Failed to create form version:', err);
          }
          auditService.log({
            actionType: 'created',
            entityType: 'landing_page',
            entityId: savedPageId,
            entityName: pageName,
            userName: currentUser.User_Name,
            userEmail: currentUser.Email,
            userRole: currentUser.Role,
            team: currentWorkspace.name,
            region: currentSubRegion || ''
          });
        })();
      }
      if (!isEditing) {
        navigate('/landing-pages');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to save landing page', {
        description: msg
      });
    } finally {
      setIsSaving(false);
    }
  };
  const handleRestoreVersion = async (version: FormVersionRecord | any) => {
    const formVersion = version as FormVersionRecord;
    setPageData({
      html: formVersion.HTML || '',
      css: formVersion.CSS || '',
      js: formVersion.JavaScript1 || ''
    });
    if (formVersion.Builder_Schema) {
      try {
        const schema = JSON.parse(formVersion.Builder_Schema);
        if (schema.blocks && schema.blocks.length > 0) {
          setBuilderBlocks(schema.blocks);
          setBuilderTheme({
            ...DEFAULT_FORM_THEME,
            ...(schema.theme || {})
          });
          setEditorMode('builder');
        } else {
          setEditorMode('code');
        }
      } catch {
        setEditorMode('code');
      }
    } else {
      setEditorMode('code');
    }
    // Save the restored content to the main record
    if (id && isEditing) {
      try {
        await landingPagesApi.update(id, {
          HTML: formVersion.HTML || '',
          CSS: formVersion.CSS || '',
          JavaScript1: formVersion.JavaScript1 || '',
          Builder_Schema: formVersion.Builder_Schema || '',
          Builder_Mode: formVersion.Builder_Schema && JSON.parse(formVersion.Builder_Schema).blocks?.length > 0 ? 'builder' : 'code'
        });
        auditService.log({
          actionType: 'restored',
          entityType: 'landing_page',
          entityId: id,
          entityName: pageName,
          summary: `Restored to Version ${formVersion.Version_Number}`,
          userName: currentUser.User_Name,
          userEmail: currentUser.Email,
          userRole: currentUser.Role,
          team: currentWorkspace.name,
          region: currentSubRegion || ''
        });
      } catch (err) {
        console.error('Failed to update record after restore:', err);
        throw err;
      }
    }
  };
  const publicUrl = urlSlug ? `${window.location.origin}/page/${urlSlug}` : '';
  const handleCopyUrl = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopiedUrl(true);
      toast.success('URL copied to clipboard');
      setTimeout(() => setCopiedUrl(false), 2000);
    }).catch(() => {
      const textarea = document.createElement('textarea');
      textarea.value = publicUrl;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
      setCopiedUrl(true);
      toast.success('URL copied to clipboard');
      setTimeout(() => setCopiedUrl(false), 2000);
    });
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-24" data-id="element-4654" data-mp="awb8sqyyymwy">
        <Loader2Icon className="h-8 w-8 text-blue-500 animate-spin" data-id="element-4655" data-mp="3jajxhnydetju" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-4656" data-mp="3jcwcbs8u0nso">
          Loading landing page...
        </span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-4657" data-mp="3sbzturscov52">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-4658" data-mp="3sct0ztv81mnq">
        <div className="flex items-center gap-4" data-id="element-4659" data-mp="3o9mxuv3eoblh">
          <Link to="/landing-pages" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-4660" data-mp="3oc97r1drr36v">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-4661" data-mp="13k74bscsy8s5" />
          </Link>
          <div data-id="element-4662" data-mp="13m0al6ji6qzm">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4663" data-mp="1b36o3z46e2c1">
              {isEditing ? 'Edit Page: ' + (pageName || '...') : 'Create Landing Page'}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-4664" data-mp="1158wd1n3xneo">
              {editorMode === 'builder' ? 'Drag and drop blocks to build your page.' : 'Build your landing page with custom HTML, CSS, and JS.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-4665" data-mp="1tvr9kvhjp55t">
          {/* Mode Toggle */}
          <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5" data-id="element-4666" data-mp="1ys801wc7vvm2">
            <button onClick={() => setEditorMode('code')} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'code' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-4667" data-mp="1k33bzyiqqx61">
              Code
            </button>
            <button onClick={() => {
            if (editorMode === 'code' && pageData.html && builderBlocks.length === 0) {
              setShowBuilderConfirm(true);
              return;
            }
            setEditorMode('builder');
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'builder' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-4668" data-mp="2hktp9xsmcnxo">
              Builder
            </button>
          </div>

          {/* View Code Button - Only in Builder mode */}
          {editorMode === 'builder' && <button onClick={() => setShowCodeModal(true)} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors" title="View Generated HTML" data-id="element-4669" data-mp="12tssvqfceyrx">
              <CodeIcon className="h-4 w-4" data-id="element-4670" data-mp="1qj3jwg6w5esv" />
              <span className="hidden sm:inline" data-id="element-4671" data-mp="1yum2fy5xxq9z">View Code</span>
            </button>}

          {isEditing && <button onClick={() => setShowVersionHistory(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-4672" data-mp="22vxu098dklkt">
              <HistoryIcon className="h-4 w-4" data-id="element-4673" data-mp="2hsy99d9dqo2e" /> History
            </button>}
          <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-4674" data-mp="2lua2rtvdjtfv">
            <EyeIcon className="h-4 w-4" data-id="element-4675" data-mp="2vj1srwgvk3o5" /> Preview
          </button>
          <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-4676" data-mp="30jg2rk21dnot">
            {isSaving ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-4677" data-mp="2frskl1cwf3dc" /> : <SaveIcon className="h-4 w-4" data-id="element-4678" data-mp="2awvfg5iqpe5q" />}
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      {editorMode === 'builder' ? <div className="space-y-6" data-id="element-4679" data-mp="2kr5c3zepsym9">
          <FormBuilder initialBlocks={builderBlocks} initialTheme={builderTheme} hideLogicRules customPalette={LANDING_PAGE_PALETTE} onSchemaChange={(blocks, theme) => {
        setBuilderBlocks(blocks);
        setBuilderTheme(theme);
      }} onHtmlChange={(html, css) => {
        setPageData(prev => ({
          ...prev,
          html,
          css
        }));
      }} data-id="element-4680" data-mp="2yk3ishwlyd54" />
          {/* Settings below builder */}
          <div className="grid grid-cols-3 gap-6" data-id="element-4681" data-mp="197rqp0h8zhnb">
            <div className="col-span-2" data-id="element-4682" data-mp="3kssad2qkx8y9" />
            <div className="space-y-6" data-id="element-4683" data-mp="3kv4zbzur8jkt">
              <SettingsCard pageName={pageName} setPageName={setPageName} urlSlug={urlSlug} setUrlSlug={setUrlSlug} slugManuallyEdited={slugManuallyEdited} setSlugManuallyEdited={setSlugManuallyEdited} status={status} setStatus={setStatus} team={team} region={region} isEditing={isEditing} data-id="element-4684" data-mp="3psmyiyiyzkfm" />
              <PublicUrlCard publicUrl={publicUrl} urlSlug={urlSlug} status={status} copiedUrl={copiedUrl} onCopy={handleCopyUrl} data-id="element-4685" data-mp="2s4cwijj6owoi" />
            </div>
          </div>
        </div> : <div className="grid grid-cols-3 gap-6" data-id="element-4686" data-mp="qb6up6wenixn">
          <div className="col-span-2 space-y-6" data-id="element-4687" data-mp="vb1l79c9q6i6">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-4688" data-mp="v9qizdyh16t6">
              <div className="border-b border-gray-200 px-4 py-3 flex items-center gap-4" data-id="element-4689" data-mp="3e9ufsvvl1w6b">
                {(['html', 'css', 'js'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={'text-sm font-medium px-3 py-1.5 rounded-md transition-colors ' + (activeTab === tab ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50')} data-id="element-4690" data-mp="35his3hrusmu1">
                    {tab === 'js' ? 'JavaScript' : tab.toUpperCase()}
                  </button>)}
              </div>
              <div className="p-4" data-id="element-4691" data-mp="2hmprosyg4ih5">
                {activeTab === 'html' && <CodeEditor label="Page HTML" language="html" placeholder="<div class='landing-page'>...</div>" value={pageData.html} onChange={e => setPageData({
              ...pageData,
              html: e.target.value
            })} data-id="element-4692" data-mp="kq0iampwg596" />}
                {activeTab === 'css' && <CodeEditor label="Custom CSS" language="css" placeholder=".landing-page { ... }" value={pageData.css} onChange={e => setPageData({
              ...pageData,
              css: e.target.value
            })} data-id="element-4693" data-mp="123i5rymovucv" />}
                {activeTab === 'js' && <CodeEditor label="Custom JavaScript" language="javascript" placeholder="// Add interactivity..." value={pageData.js} onChange={e => setPageData({
              ...pageData,
              js: e.target.value
            })} data-id="element-4694" data-mp="1pqghkizv6e4g" />}
              </div>
            </div>

            {/* GPT Code Assistant */}
            <GptCodeAssistant mode="form" currentCode={pageData} onCodeGenerated={code => {
          setPageData(prev => ({
            html: code.html || prev.html,
            css: code.css !== undefined ? code.css : prev.css,
            js: code.js !== undefined ? code.js : prev.js
          }));
        }} data-id="element-4695" data-mp="28jjjaurd7q0p" />
          </div>

          <div className="space-y-6" data-id="element-4696" data-mp="2w7t1znta7xnm">
            <SettingsCard pageName={pageName} setPageName={setPageName} urlSlug={urlSlug} setUrlSlug={setUrlSlug} slugManuallyEdited={slugManuallyEdited} setSlugManuallyEdited={setSlugManuallyEdited} status={status} setStatus={setStatus} team={team} region={region} isEditing={isEditing} data-id="element-4697" data-mp="2w6rn1895ar0m" />
            <PublicUrlCard publicUrl={publicUrl} urlSlug={urlSlug} status={status} copiedUrl={copiedUrl} onCopy={handleCopyUrl} data-id="element-4698" data-mp="auo5se63f2v5" />

            {/* Tips Card */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3" data-id="element-4699" data-mp="1rveiawwur5b">
              <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4700" data-mp="3t09i1qk9fdi9">
                How to use
              </h3>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 space-y-2 text-xs text-blue-700" data-id="element-4701" data-mp="1r8e8phlh7l37">
                <p data-id="element-4702" data-mp="1m8ry7fx18iy3">
                  <strong data-id="element-4703" data-mp="1macsd4ffpzvf">1.</strong> Build your landing page using the code
                  editor or builder
                </p>
                <p data-id="element-4704" data-mp="3go769g3rcika">
                  <strong data-id="element-4705" data-mp="3gnoypnbnahx2">2.</strong> Set the status to <strong data-id="element-4706" data-mp="3lgi3gb745q95">Active</strong>{' '}
                  and save
                </p>
                <p data-id="element-4707" data-mp="36ru9uagtvqlu">
                  <strong data-id="element-4708" data-mp="36snb4vx0pnwl">3.</strong> Go to{' '}
                  <strong data-id="element-4709" data-mp="36phnzm7y8w3i">Email Templates → Edit</strong> and paste the public
                  URL into the <strong data-id="element-4710" data-mp="3bo2n1ibgt9qd">"Redirect After Confirmation"</strong>{' '}
                  field
                </p>
                <p data-id="element-4711" data-mp="89oq0r3nxwpl">
                  <strong data-id="element-4712" data-mp="8a89wdcviv69">4.</strong> When subscribers confirm, they'll be
                  redirected to your custom landing page
                </p>
              </div>
              <p className="text-xs text-gray-500" data-id="element-4713" data-mp="3qhnwka4orfj7">
                You can also select a landing page directly from the email
                template editor's redirect dropdown.
              </p>
            </div>
          </div>
        </div>}

      <PreviewModal isOpen={showPreview} onClose={() => setShowPreview(false)} title="Landing Page Preview" data-id="element-4714" data-mp="2j2t2kqjqeli5">
        <FormPreview html={pageData.html} css={pageData.css} js={pageData.js} data-id="element-4715" data-mp="2bk1uw9gnpt8e" />
      </PreviewModal>

      <ViewCodeModal isOpen={showCodeModal} onClose={() => setShowCodeModal(false)} html={pageData.html} data-id="element-4716" data-mp="26li6m2elc5cv" />

      <ConfirmModal isOpen={showBuilderConfirm} title="Switch to Builder" message="The builder starts with a blank canvas — your existing code cannot be imported into the visual builder. However, your code is preserved and will still be there if you switch back to Code mode without saving." confirmLabel="Switch to Builder" variant="warning" onCancel={() => setShowBuilderConfirm(false)} onConfirm={() => {
      setShowBuilderConfirm(false);
      setEditorMode('builder');
    }} data-id="element-4717" data-mp="2gh20ykdiavzi" />

      <VersionHistoryPanel isOpen={showVersionHistory} onClose={() => setShowVersionHistory(false)} entityType="form" entityId={id || ''} entityName={pageName || 'Landing Page'} onRestore={handleRestoreVersion} data-id="element-4718" data-mp="19ywr2pl7s3p2" />
    </div>;
}
// ── Settings Card ──
function SettingsCard({
  pageName,
  setPageName,
  urlSlug,
  setUrlSlug,
  slugManuallyEdited,
  setSlugManuallyEdited,
  status,
  setStatus,
  team,
  region,
  isEditing
}: {
  pageName: string;
  setPageName: (v: string) => void;
  urlSlug: string;
  setUrlSlug: (v: string) => void;
  slugManuallyEdited: boolean;
  setSlugManuallyEdited: (v: boolean) => void;
  status: string;
  setStatus: (v: string) => void;
  team: string;
  region: string;
  isEditing: boolean;
}) {
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4" data-id="element-4719" data-mp="102qwoducxcve">
      <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4720" data-mp="15095m48djs3e">
        Settings
      </h3>
      <div className="space-y-2" data-id="element-4721" data-mp="31vf30w2asgfe">
        <label className="block text-sm font-medium text-gray-700" data-id="element-4722" data-mp="36rvn9f6dman7">
          Page Name
        </label>
        <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={pageName} onChange={e => setPageName(e.target.value)} placeholder="Thank You Page" data-id="element-4723" data-mp="2z9ecz6qoert6" />
      </div>
      <div className="space-y-2" data-id="element-4724" data-mp="2kkrl65jv47l2">
        <label className="block text-sm font-medium text-gray-700" data-id="element-4725" data-mp="2klksp8qnb149">
          URL Slug
        </label>
        <div className="flex rounded-md shadow-sm" data-id="element-4726" data-mp="3i6z0oahpfkc7">
          <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-4727" data-mp="3d85oqmi2g1e5">
            /page/
          </span>
          <input type="text" className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={urlSlug} onChange={e => {
          setUrlSlug(e.target.value);
          setSlugManuallyEdited(true);
        }} placeholder="thank-you" data-id="element-4728" data-mp="347zbn1qa53fi" />
        </div>
        {!slugManuallyEdited && !isEditing && pageName && <p className="text-xs text-gray-400" data-id="element-4729" data-mp="1z6zq5cjmtk0e">Auto-generated from page name</p>}
      </div>
      <div className="space-y-2" data-id="element-4730" data-mp="1kdw7t2y7v28m">
        <label className="block text-sm font-medium text-gray-700" data-id="element-4731" data-mp="1pad7mues351j">
          Status
        </label>
        <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={status} onChange={e => setStatus(e.target.value)} data-id="element-4732" data-mp="1biyazkry75c0">
          <option value="active" data-id="element-4733" data-mp="11mmvyn0z41dv">Active</option>
          <option value="draft" data-id="element-4734" data-mp="16keq7np3bzl8">Draft</option>
          <option value="archived" data-id="element-4735" data-mp="16l6l0wav5tag">Archived</option>
        </select>
      </div>
      <div className="space-y-2" data-id="element-4736" data-mp="skdns3t6gk3n">
        <label className="block text-sm font-medium text-gray-700" data-id="element-4737" data-mp="sl5i7bb1gbsc">Team</label>
        <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-4738" data-mp="wl61paczvd69" />
        <p className="text-xs text-gray-400" data-id="element-4739" data-mp="90euyb8f1b45">
          Automatically set from your current workspace
        </p>
      </div>
      <div className="space-y-2" data-id="element-4740" data-mp="mw8s5wztuc1f">
        <label className="block text-sm font-medium text-gray-700" data-id="element-4741" data-mp="hz0jqo4hdmdd">
          Region
        </label>
        <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={region || '(All regions)'} disabled data-id="element-4742" data-mp="ww9hq0oahoe2" />
        <p className="text-xs text-gray-400" data-id="element-4743" data-mp="15ol67gzuf1re">
          Automatically set from your current scope selection
        </p>
      </div>
    </div>;
}
// ── Public URL Card ──
function PublicUrlCard({
  publicUrl,
  urlSlug,
  status,
  copiedUrl,
  onCopy
}: {
  publicUrl: string;
  urlSlug: string;
  status: string;
  copiedUrl: boolean;
  onCopy: () => void;
}) {
  if (!urlSlug) return null;
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3" data-id="element-4744" data-mp="2i03k46xh7f1v">
      <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4745" data-mp="2m0xd3eprzk1m">
        Public URL
      </h3>
      <div className="flex items-center gap-2" data-id="element-4746" data-mp="2r24fszd29t1f">
        <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm font-mono text-xs text-gray-600" value={publicUrl} readOnly onClick={e => (e.target as HTMLInputElement).select()} data-id="element-4747" data-mp="2w0xwf0nna05t" />
        <button onClick={onCopy} className="inline-flex items-center gap-1 px-2.5 py-2 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors flex-shrink-0" data-id="element-4748" data-mp="28b3jyk78mdm3">
          {copiedUrl ? <CheckIcon className="h-3.5 w-3.5" data-id="element-4749" data-mp="1flmunvewzcu3" /> : <CopyIcon className="h-3.5 w-3.5" data-id="element-4750" data-mp="1kkhe8zkph5f9" />}
          {copiedUrl ? 'Copied!' : 'Copy'}
        </button>
      </div>
      {status?.toLowerCase() !== 'active' && <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md" data-id="element-4751" data-mp="182wu8te2ys6x">
          <svg className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-4752" data-mp="133ahsm62tfz6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-4753" data-mp="u3duoozc4p68" />
          </svg>
          <p className="text-xs text-amber-800" data-id="element-4754" data-mp="kcszlbive2s2">
            This page is not active. Set status to <strong data-id="element-4755" data-mp="fdzkjenxq3ps">Active</strong> for
            it to be publicly accessible.
          </p>
        </div>}
      {status?.toLowerCase() === 'active' && <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 font-medium" data-id="element-4756" data-mp="ae3i3juvykcc">
          <ExternalLinkIcon className="h-3.5 w-3.5" data-id="element-4757" data-mp="22l3ophf42kuf" />
          Open in new tab →
        </a>}
    </div>;
}